import React, {useCallback, useContext, useRef, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {createSortPathConfiguration} from "../../resources/SortPathSettingsResource";
import {scheduleUploadedConfig, unwrapErrorMessage} from "../../helper/BulkUploadScheduleHelper";
import {getNodeId, isConfigEnabled} from "../../Utility";
import {StationConfigContext} from "../../context/StationConfigContext";
import {PATH_SCHEDULING_ENABLED} from "../../Constants";
import {
    parseCSV,
    parseClusterParamsCSV,
    validateRow,
    validateDuplicates,
    validateCycleCapacity,
    buildSortPathSettingFromRow,
    lookupSettingId,
    resolveRowName,
    getEffectiveDate,
} from "../../helper/BulkSortGridAllocationHelper";
import { SORT_PATH_CSV_HEADERS, COLORS, ACTION_LABELS, STATUS_LABELS, SCHEDULE_STATUS_LABELS, ERROR_DETAILS } from "../../constants/SortPathBulkUploadConstants";

/**
 * Extracts a readable error message from an API response.
 * The errorMessage field may be a JSON string with a nested message, or a plain string.
 */
function extractErrorMessage(response) {
    return unwrapErrorMessage(response && response.errorMessage);
}

/**
 * en.json message ids for each schedule status label (the label is the default message).
 */
const SCHEDULE_STATUS_MESSAGE_IDS = {
    [SCHEDULE_STATUS_LABELS.SCHEDULED]: "sortPaths.bulkUpload.scheduleStatus.scheduled",
    [SCHEDULE_STATUS_LABELS.FAILED]: "sortPaths.bulkUpload.scheduleStatus.failed",
    [SCHEDULE_STATUS_LABELS.NOT_SCHEDULED]: "sortPaths.bulkUpload.scheduleStatus.notScheduled",
};

/**
 * Maps a schedule status label to its display color.
 * NOT_SCHEDULED is neutral (no color) since it is an expected outcome for rows without an effective_date.
 */
function getScheduleStatusColor(scheduleStatus) {
    if (scheduleStatus === SCHEDULE_STATUS_LABELS.SCHEDULED) return COLORS.SCHEDULED;
    if (scheduleStatus === SCHEDULE_STATUS_LABELS.FAILED) return COLORS.FAILED;
    return undefined;
}

/**
 * Renders a progress bar showing upload completion percentage.
 */
const ProgressBar = ({current, total}) => {
    const percent = Math.round((current / total) * 100);
    return (
        <Column spacing="xsmall">
            <Row alignmentHorizontal="center">
                <Text type="b300">
                    <FormattedMessage id="sortPaths.bulkUploadProgress" defaultMessage="Uploading {current}/{total}" values={{current, total}}/>
                </Text>
            </Row>
            <div
                role="progressbar"
                aria-label="Upload progress"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{width: "100%", backgroundColor: COLORS.PROGRESS_BG, borderRadius: "4px", height: "8px"}}
            >
                <div style={{
                    width: `${percent}%`,
                    backgroundColor: COLORS.PROGRESS_BAR,
                    height: "8px",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                }}/>
            </div>
        </Column>
    );
};

/**
 * Bulk Upload modal component for Sort Grid Allocation configurations.
 * Handles CSV file selection, validation, and sequential upload of sort path settings.
 */
const BulkSortGridAllocationUpload = ({intl, onComplete, sortPathConfigurationList, cycleList}) => {
    const {state} = useContext(StationConfigContext);
    // Future-date scheduling is a gated capability. When pathSchedulingEnabled is off, rows
    // still create their config but scheduling is skipped (reported NOT_SCHEDULED), matching
    // how the Path Schedule tab itself is gated (SortPathTabs).
    const isPathSchedulingEnabled = isConfigEnabled(PATH_SCHEDULING_ENABLED, state.stationConfigs);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [uploadResults, setUploadResults] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [rawRows, setRawRows] = useState([]);
    const [clusterParamsMap, setClusterParamsMap] = useState(null);
    const [mainFileName, setMainFileName] = useState("");
    const [clusterFileName, setClusterFileName] = useState("");
    const [isValidated, setIsValidated] = useState(false);
    const [parseErrors, setParseErrors] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({current: 0, total: 0});
    const mainFileInputRef = useRef(null);
    const clusterFileInputRef = useRef(null);

    /**
     * Resets all component state to initial values.
     */
    const resetState = useCallback(() => {
        setValidationErrors([]);
        setUploadResults([]);
        setParsedRows([]);
        setRawRows([]);
        setClusterParamsMap(null);
        setMainFileName("");
        setClusterFileName("");
        setIsUploading(false);
        setIsValidated(false);
        setParseErrors([]);
        setUploadProgress({current: 0, total: 0});
        if (mainFileInputRef.current) mainFileInputRef.current.value = "";
        if (clusterFileInputRef.current) clusterFileInputRef.current.value = "";
    }, []);

    /**
     * Closes the modal and triggers onComplete callback if any uploads succeeded.
     */
    const onCloseModal = useCallback(() => {
        if (isUploading) return;
        setIsModalOpen(false);
        if (uploadResults.some(r => r.success)) {
            onComplete();
        }
        resetState();
    }, [isUploading, uploadResults, onComplete, resetState]);

    /**
     * Reads the main configuration CSV file and parses it into raw rows.
     */
    const handleMainFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        setParseErrors([]);
        setValidationErrors([]);
        setUploadResults([]);
        setParsedRows([]);
        setRawRows([]);
        setIsValidated(false);
        setMainFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = parseCSV(e.target.result);
            if (result.error) {
                setParseErrors([result.error]);
                return;
            }
            setRawRows(result.rows);
        };
        reader.onerror = () => {
            setParseErrors(["Failed to read file. Please try again or use a different file."]);
        };
        reader.readAsText(file);
    }, []);

    /**
     * Reads the optional cluster parameters CSV and parses it into a map keyed by nodeId#cycle.
     */
    const handleClusterFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        setParseErrors(prev => prev.filter(e => !e.startsWith('Cluster params:')));
        setClusterFileName(file.name);
        setIsValidated(false);
        const reader = new FileReader();
        reader.onload = (e) => {
            const result = parseClusterParamsCSV(e.target.result);
            if (result.error) {
                setParseErrors(prev => [...prev, `Cluster params: ${result.error}`]);
                return;
            }
            setClusterParamsMap(result.clusterParamsMap);
        };
        reader.onerror = () => {
            setParseErrors(prev => [...prev, "Cluster params: Failed to read file. Please try again or use a different file."]);
        };
        reader.readAsText(file);
    }, []);

    /**
     * Validates all parsed rows for duplicates, required fields, and algorithm constraints.
     */
    const handleValidate = useCallback(() => {
        const errors = [];
        const currentNodeId = getNodeId();
        // Check for duplicate cycle entries
        const dupErrors = validateDuplicates(rawRows, isPathSchedulingEnabled);
        if (dupErrors.length > 0) {
            setValidationErrors(dupErrors);
            setIsValidated(false);
            return;
        }
        // Per-cycle cap on sort path configurations (multi-config only)
        const capacityErrors = validateCycleCapacity(rawRows, sortPathConfigurationList, isPathSchedulingEnabled);
        if (capacityErrors.length > 0) {
            setValidationErrors(capacityErrors);
            setIsValidated(false);
            return;
        }
        rawRows.forEach(row => {
            const error = validateRow(row, clusterParamsMap, sortPathConfigurationList, currentNodeId, cycleList, isPathSchedulingEnabled);
            if (error) errors.push(error);
        });

        if (errors.length > 0) {
            setValidationErrors(errors);
            setIsValidated(false);
        } else {
            setValidationErrors([]);
            setParsedRows(rawRows);
            setIsValidated(true);
        }
    }, [rawRows, clusterParamsMap, sortPathConfigurationList, cycleList, isPathSchedulingEnabled]);

    /**
     * Sequentially uploads each validated row as a sort path configuration.
     * Tracks success/failure per row and updates progress.
     */
    const handleUpload = useCallback(async () => {
        setIsUploading(true);
        setUploadProgress({current: 0, total: parsedRows.length});
        const results = [];

        for (let i = 0; i < parsedRows.length; i++) {
            const row = parsedRows[i];
            setUploadProgress({current: i + 1, total: parsedRows.length});
            const sortPathSetting = buildSortPathSettingFromRow(row, clusterParamsMap, sortPathConfigurationList, isPathSchedulingEnabled);
            const isUpdate = !!sortPathSetting.settingId;
            const effectiveDate = getEffectiveDate(row);

            // Each row tracks two independent outcomes: the configuration create/update,
            // and (only when an effective_date is present) the scheduling of that config.
            const result = {
                row: row._rowNumber,
                name: sortPathSetting.name,
                cycle: row[SORT_PATH_CSV_HEADERS.CYCLE],
                isUpdate,
                effectiveDate,
                // configuration outcome
                success: false,
                error: undefined,
                // scheduling outcome — independent of configuration outcome.
                // Defaults to NOT_SCHEDULED; only the scheduling branch below (which runs
                // when config succeeded AND a date is present) sets SCHEDULED or FAILED.
                // A row whose config fails is left NOT_SCHEDULED since scheduling was never attempted.
                scheduleStatus: SCHEDULE_STATUS_LABELS.NOT_SCHEDULED,
                scheduleError: undefined,
            };

            // Step 1: create/update the configuration.
            let configResponse;
            try {
                configResponse = await createSortPathConfiguration(getNodeId(), sortPathSetting);
                if (configResponse && configResponse.sortPathSetting) {
                    result.success = true;
                } else {
                    result.error = extractErrorMessage(configResponse);
                }
            } catch (error) {
                result.error = error.toString();
            }

            // Step 2: schedule, only if the config succeeded, an effective_date was given, AND
            // the pathSchedulingEnabled capability is on for this station. When the capability
            // is off, the config still lands but scheduling is skipped and the row is left
            // NOT_SCHEDULED (the default), so the date is simply ignored rather than rejected.
            // Scheduling success/failure is recorded separately so a saved-but-not-scheduled
            // configuration is reported honestly.
            if (result.success && effectiveDate && isPathSchedulingEnabled) {
                const {scheduleStatus, scheduleError} = await scheduleUploadedConfig(configResponse, row, effectiveDate);
                result.scheduleStatus = scheduleStatus;
                result.scheduleError = scheduleError;
            }

            results.push(result);
        }

        setUploadResults(results);
        setIsUploading(false);
    }, [parsedRows, clusterParamsMap, sortPathConfigurationList, isPathSchedulingEnabled]);

    const successCount = uploadResults.filter(r => r.success).length;
    const failureCount = uploadResults.filter(r => !r.success).length;
    // Scheduling counts are independent of configuration counts: a row can save its
    // config successfully yet fail to schedule.
    const scheduledCount = uploadResults.filter(r => r.scheduleStatus === SCHEDULE_STATUS_LABELS.SCHEDULED).length;
    const scheduleFailureCount = uploadResults.filter(r => r.scheduleStatus === SCHEDULE_STATUS_LABELS.FAILED).length;

    return (
        <>
            <Button onClick={() => setIsModalOpen(true)}>
                <FormattedMessage id="sortPaths.bulkUpload" defaultMessage="Bulk Upload CSV"/>
            </Button>

            <Modal
                title={intl.formatMessage({id: "sortPaths.bulkUploadTitle", defaultMessage: "Bulk Upload Sort Grid Allocations"})}
                open={isModalOpen}
                onClose={onCloseModal}
                closable={!isUploading}
                width="1000px"
                scrollContainer="modal"
            >
                <Column spacing="medium">
                    {/* File inputs */}
                    {uploadResults.length === 0 && (
                        <Column spacing="small">
                            <Text type="b300">
                                <FormattedMessage id="sortPaths.bulkUploadInstructions"
                                    defaultMessage="Upload a main configuration CSV. Optionally, upload a cluster parameters CSV with per-cluster settings."/>
                            </Text>

                            {/* Main CSV */}
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.mainCsvLabel" defaultMessage="Main Configuration CSV (required)"/>
                                </Text>
                                <input
                                    ref={mainFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleMainFileSelect}
                                    data-testid="bulkUploadMainFileInput"
                                />
                                {mainFileName && <Text type="b200" color="secondary">{mainFileName}</Text>}
                            </Column>

                            {/* Cluster params CSV */}
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.clusterCsvLabel"
                                        defaultMessage="Cluster Parameters CSV (for STAG and AMZL sites)"/>
                                </Text>
                                <input
                                    ref={clusterFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleClusterFileSelect}
                                    data-testid="bulkUploadClusterFileInput"
                                />
                                {clusterFileName && <Text type="b200" color="secondary">{clusterFileName}</Text>}
                            </Column>
                        </Column>
                    )}

                    {/* Parse errors */}
                    {parseErrors.length > 0 && (
                        <Alert type="error">
                            <Column spacing="xsmall">
                                {parseErrors.map((err, i) => (
                                    <Text key={i} type="b200">{err}</Text>
                                ))}
                            </Column>
                        </Alert>
                    )}

                    {/* Validation errors */}
                    {validationErrors.length > 0 && (
                        <Alert type="error">
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.bulkValidationErrors" defaultMessage="Validation errors found:"/>
                                </Text>
                                {validationErrors.map((err, i) => (
                                    <Text key={i} type="b200">{err}</Text>
                                ))}
                            </Column>
                        </Alert>
                    )}

                    {/* Progress bar during upload — replaces the info alert */}
                    {isUploading && (
                        <ProgressBar current={uploadProgress.current} total={uploadProgress.total}/>
                    )}

                    {/* Parsed rows preview */}
                    {isValidated && parsedRows.length > 0 && uploadResults.length === 0 && (
                        <Column spacing="small">
                            {!isUploading && (
                                <Alert type="success">
                                    <FormattedMessage id="sortPaths.bulkRowsParsed"
                                        defaultMessage="{count} configuration(s) validated.{clusterInfo} Click Upload to proceed."
                                        values={{
                                            count: parsedRows.length,
                                            clusterInfo: clusterParamsMap
                                                ? ` Cluster params loaded for ${Object.keys(clusterParamsMap).length} cycle(s).`
                                                : "",
                                        }}/>
                                </Alert>
                            )}
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>Cycle</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Fill Algorithm</TableCell>
                                    <TableCell>Pick Algorithm</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell><FormattedMessage id="sortPaths.bulkUpload.effectiveDate" defaultMessage="Effective Date"/></TableCell>
                                </TableRow>
                                {parsedRows.map(row => {
                                    // Same create/update resolution as the upload (name-aware with multi-config).
                                    const hasExisting = !!lookupSettingId(
                                        row[SORT_PATH_CSV_HEADERS.NODE_ID], row[SORT_PATH_CSV_HEADERS.CYCLE],
                                        sortPathConfigurationList, resolveRowName(row), isPathSchedulingEnabled);
                                    const effectiveDate = getEffectiveDate(row);
                                    return (
                                        <TableRow key={row._rowNumber}>
                                            <TableCell>{row._rowNumber}</TableCell>
                                            <TableCell>{row[SORT_PATH_CSV_HEADERS.CYCLE]}</TableCell>
                                            <TableCell>{resolveRowName(row)}</TableCell>
                                            <TableCell>{row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM]}</TableCell>
                                            <TableCell>{row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM]}</TableCell>
                                            <TableCell>
                                                <span style={{color: hasExisting ? COLORS.UPDATE : COLORS.CREATE, fontWeight: "bold"}}>
                                                    {hasExisting ? ACTION_LABELS.UPDATE : ACTION_LABELS.CREATE}
                                                </span>
                                            </TableCell>
                                            <TableCell>{effectiveDate || (isPathSchedulingEnabled
                                                // With path scheduling no CycleMapping is written, so an undated row is saved but not activated.
                                                ? <FormattedMessage id="sortPaths.bulkUpload.notScheduled" defaultMessage="Not scheduled"/>
                                                : <FormattedMessage id="sortPaths.bulkUpload.immediate" defaultMessage="Immediate"/>)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        </Column>
                    )}

                    {/* Upload results */}
                    {uploadResults.length > 0 && (
                        <Column spacing="small">
                            <Alert type={(failureCount === 0 && scheduleFailureCount === 0) ? "success" : "warning"}>
                                <Column spacing="xsmall">
                                    <FormattedMessage id="sortPaths.bulkUploadSummary"
                                        defaultMessage="{success} succeeded, {failure} failed out of {total} configurations."
                                        values={{success: successCount, failure: failureCount, total: uploadResults.length}}/>
                                    {(scheduledCount > 0 || scheduleFailureCount > 0) && (
                                        <FormattedMessage id="sortPaths.bulkScheduleSummary"
                                            defaultMessage="Scheduling: {scheduled} scheduled, {scheduleFailure} failed."
                                            values={{scheduled: scheduledCount, scheduleFailure: scheduleFailureCount}}/>
                                    )}
                                </Column>
                            </Alert>
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>Cycle</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell><FormattedMessage id="sortPaths.bulkUpload.configStatus" defaultMessage="Config Status"/></TableCell>
                                    <TableCell><FormattedMessage id="sortPaths.bulkUpload.scheduleStatus" defaultMessage="Schedule Status"/></TableCell>
                                    <TableCell>Error Details</TableCell>
                                </TableRow>
                                {uploadResults.map(result => {
                                    // Combine config and schedule errors so both are visible per row.
                                    const errorParts = [];
                                    if (result.error) errorParts.push(ERROR_DETAILS.CONFIG_PREFIX + result.error);
                                    if (result.scheduleError) errorParts.push(ERROR_DETAILS.SCHEDULE_PREFIX + result.scheduleError);
                                    return (
                                        <TableRow key={result.row}>
                                            <TableCell>{result.row}</TableCell>
                                            <TableCell>{result.cycle}</TableCell>
                                            <TableCell>{result.name}</TableCell>
                                            <TableCell>
                                                <span style={{color: result.isUpdate ? COLORS.UPDATE : COLORS.CREATE, fontWeight: "bold"}}>
                                                    {result.isUpdate ? ACTION_LABELS.UPDATE : ACTION_LABELS.CREATE}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span style={{color: result.success ? COLORS.SUCCESS : COLORS.FAILED, fontWeight: "bold"}}>
                                                    {result.success ? STATUS_LABELS.SUCCESS : STATUS_LABELS.FAILED}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span style={{color: getScheduleStatusColor(result.scheduleStatus), fontWeight: "bold"}}>
                                                    <FormattedMessage id={SCHEDULE_STATUS_MESSAGE_IDS[result.scheduleStatus]}
                                                        defaultMessage={result.scheduleStatus}/>
                                                </span>
                                            </TableCell>
                                            <TableCell>{errorParts.length > 0 ? errorParts.join(ERROR_DETAILS.SEPARATOR) : ERROR_DETAILS.EMPTY}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        </Column>
                    )}
                </Column>

                <ModalFooter>
                    <Row alignmentHorizontal="end" spacing="small">
                        <Button type="secondary" onClick={onCloseModal}>
                            <FormattedMessage id="sortPaths.close" defaultMessage="Close"/>
                        </Button>
                        {rawRows.length > 0 && !isValidated && uploadResults.length === 0 && (
                            <Button onClick={handleValidate}>
                                <FormattedMessage id="sortPaths.validate" defaultMessage="Validate"/>
                            </Button>
                        )}
                        {isValidated && parsedRows.length > 0 && uploadResults.length === 0 && !isUploading && (
                            <Button onClick={handleUpload} disabled={isUploading}>
                                <FormattedMessage id="sortPaths.upload" defaultMessage="Upload"/>
                            </Button>
                        )}
                    </Row>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default injectIntl(BulkSortGridAllocationUpload);
